import { z } from 'zod'
import { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { EmcConfigDto } from 'src/Libs/flow/installation/api'
import { EM360_DEFAULT_MODBUS_SETTINGS } from 'src/Libs/installations/constants'

export type Em360ModbusProtocol = 'e3dc' | 'enerfin' | 'sunspec'

export interface Em360ModbusFormModel {
  modbusEnabled: boolean
  modbusProtocol: Em360ModbusProtocol
  modbusPort: number
}

export const useEm360ModbusFormInitializer = (
  config?: Nullable<EmcConfigDto>
): { initialValues: Em360ModbusFormModel; validationSchema: z.ZodType<Em360ModbusFormModel> } => {
  const { t } = useCustomTranslation()

  const initialValues: Em360ModbusFormModel = useMemo(
    () => ({
      modbusEnabled: config?.modbusEnabled ?? EM360_DEFAULT_MODBUS_SETTINGS.modbusEnabled,
      modbusProtocol: config?.modbusProtocol ?? EM360_DEFAULT_MODBUS_SETTINGS.modbusProtocol,
      modbusPort: config?.modbusPort ?? EM360_DEFAULT_MODBUS_SETTINGS.modbusPort,
    }),
    [config?.modbusEnabled, config?.modbusProtocol, config?.modbusPort]
  )

  const validationSchema: z.ZodType<Em360ModbusFormModel> = useMemo(
    () =>
      z.object({
        modbusEnabled: z.boolean(),
        modbusProtocol: z.enum(['e3dc', 'enerfin', 'sunspec']),
        modbusPort: z
          .number({
            required_error: t('installation-configuration.em360.modbus-tcp.port-required', 'Port is required'),
            invalid_type_error: t('installation-configuration.em360.modbus-tcp.port-invalid', 'Port must be a number'),
          })
          .int(t('installation-configuration.em360.modbus-tcp.port-invalid', 'Port must be a number'))
          .min(0, t('installation-configuration.em360.modbus-tcp.port-range', 'Port must be between 0 and 65535'))
          .max(65535, t('installation-configuration.em360.modbus-tcp.port-range', 'Port must be between 0 and 65535')),
      }),
    [t]
  )

  return { initialValues, validationSchema }
}
